import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import useTranslation from './useTranslation';
import LocalIcon from './LocalIcon';
// Utilizzo di React.lazy per il caricamento ritardato di PoiCardLite
const PoiCardLite = React.lazy(() => import('./poicardlite'));


// Memo per l'icona per evitare re-render non necessari
const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

// Funzione di utilità per calcolare la distanza, estratta dal componente per migliorare la pulizia del codice
const calculateDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
  if (!isFinite(lat1) || !isFinite(lon1) || !isFinite(lat2) || !isFinite(lon2)) {
    return Infinity;
  }
  
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Componente di fallback per il caricamento lazy
const CardPlaceholder = () => (
  <div className="card border shadow-sm position-relative align-items-start p-0" 
       style={{ 
         borderRadius: '16px',
         overflow: 'hidden',
         height: '200px',
         background: '#f8f9fa'
       }}>
    <div className="d-flex justify-content-center align-items-center h-100">
      <div className="spinner-border text-secondary" role="status">
        <span className="visually-hidden">Caricamento...</span>
      </div>
    </div>
  </div>
);

const PoiSlider = ({ currentPoi, allPois, taxonomyData, onPoiChange }) => {
  const { t } = useTranslation();
  const [nearbyPois, setNearbyPois] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  const sliderRef = React.useRef(null);
  const navigateTo = useNavigate();

  // Funzione per calcolare la distanza tra due punti - memoizzata
  const calculateDistance = useCallback(calculateDistanceBetweenPoints, []);

  // Ottieni la posizione dell'utente
  useEffect(() => {
    if (!navigator.geolocation) return;

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (isFinite(latitude) && isFinite(longitude)) {
          setUserLocation({ latitude, longitude });
        }
      },
      (error) => {
        console.error('Errore geolocalizzazione:', error);
        setUserLocation(prev => prev);
      },
      options
    );

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
}, []);

  // Memorizza i poi filtrati per evitare ricalcoli non necessari
  const filteredPois = useMemo(() => {
    if (!currentPoi?.field_geolocalizzazione?.[0] || !allPois?.length) return [];
    
    const currentPoiId = String(currentPoi.nid?.[0]?.value);
    
    return allPois.filter(poi => {
      const poiId = String(poi.nid?.[0]?.value);
      return poiId !== currentPoiId && poi.field_geolocalizzazione?.[0];
    });
  }, [currentPoi, allPois]);

  // Calcola i POI vicini all'attuale - ottimizzato con useMemo
  const calculateNearbyPois = useCallback(() => {
    if (!currentPoi?.field_geolocalizzazione?.[0] || !filteredPois.length) return [];

    const currentLat = currentPoi.field_geolocalizzazione[0].lat;
    const currentLng = currentPoi.field_geolocalizzazione[0].lng;

    return filteredPois
      .map(poi => {
        // Calcola la distanza dal POI corrente per l'ordinamento
        const distanceFromCurrent = calculateDistance(
          currentLat,
          currentLng,
          poi.field_geolocalizzazione[0].lat,
          poi.field_geolocalizzazione[0].lng
        );

        // Calcola la distanza dall'utente se disponibile
        const distanceFromUser = userLocation ? calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          poi.field_geolocalizzazione[0].lat,
          poi.field_geolocalizzazione[0].lng
        ) : null;

        return {
          ...poi,
          distanceFromCurrent,
          distanceFromUser
        };
      })
      .sort((a, b) => a.distanceFromCurrent - b.distanceFromCurrent)
      .slice(0, 6);
  }, [currentPoi, filteredPois, calculateDistance, userLocation]);

  // Memorizza i POI nelle vicinanze
  const memoizedNearbyPois = useMemo(() => {
    if (currentPoi && filteredPois.length > 0) {
      return calculateNearbyPois();
    }
    return [];
  }, [currentPoi, filteredPois, calculateNearbyPois]);

  // Gesture scroll handler
  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const scrollPosition = slider.scrollLeft;
    const cardWidth = slider.offsetWidth;
    const newIndex = Math.round(scrollPosition / cardWidth);
    
    setCanScrollLeft(scrollPosition > 0);
    setCanScrollRight(
      scrollPosition < slider.scrollWidth - slider.clientWidth - 1
    );

    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < nearbyPois.length) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, nearbyPois.length]);

  // Scroll to index
  const scrollToIndex = useCallback((index) => {
    if (!sliderRef.current || index < 0 || index >= nearbyPois.length) return;
    
    const slider = sliderRef.current;
    const cardWidth = slider.offsetWidth;
    const scrollPosition = cardWidth * index;
    
    slider.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    
    setActiveIndex(index);
  }, [nearbyPois.length]);

  // Aggiorna i POI vicini quando cambiano i dati di input
  useEffect(() => {
    setNearbyPois(memoizedNearbyPois);
  }, [memoizedNearbyPois]);

  // Gestione scroll
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    handleScroll();
    
    // Uso di options per migliorare le prestazioni
    slider.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  // Memorizza la funzione renderPoiCard per evitare di ricrearla a ogni render
  const renderPoiCard = useCallback((poi) => (
    <div key={poi.nid[0].value} className="card-container px-1">
      <Suspense fallback={<CardPlaceholder />}>
        <PoiCardLite 
          poi={poi}
          taxonomyData={taxonomyData}
          distance={poi.distanceFromUser ? `${poi.distanceFromUser.toFixed(1)} km` : '...'}
          showCloseButton={false}
        />
      </Suspense>
    </div>
  ), [taxonomyData]);

  // Memorizza gli indicatori per evitare ricalcoli
  const indicators = useMemo(() => {
    return nearbyPois.map((_, index) => (
      <button
        key={index}
        onClick={() => scrollToIndex(index)}
        className="btn p-0 border-0"
        style={{
          width: index === activeIndex ? '24px' : '8px',
          height: '8px',
          borderRadius: '4px',
          backgroundColor: index === activeIndex ? '#6c757d' : '#D1D5DB',
          transition: 'all 0.3s ease',
          padding: 0,
          border: 'none'
        }}
        aria-label={`${t('slide')} ${index + 1}`}
      />
    ));
  }, [nearbyPois, activeIndex, scrollToIndex, t]);

  // Rendering condizionale ottimizzato
  if (nearbyPois.length === 0) return null;

  return (
    <section className="mb-5">
      <h3 className="section-title mb-1 px-3">{t('nelleVicinanze')}</h3>
      <p className="mb-0 px-3 mb-3">{t('scopriLuoghiVicini')}</p>
      
      <div className="position-relative">
        {canScrollLeft && (
          <button
            className="btn bg-white border rounded-circle btn-scroll shadow-sm position-absolute start-0 top-50 translate-middle-y d-flex align-items-center justify-content-center opacity-75"
            onClick={() => scrollToIndex(activeIndex - 1)}
            aria-label={t('precedente')}
          >
            <Icon name="chevron_left" style={{ fontSize: '20px' }} />
          </button>
        )}
        
        {canScrollRight && (
          <button
            className="btn bg-white border rounded-circle btn-scroll shadow-sm position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center opacity-75"
            onClick={() => scrollToIndex(activeIndex + 1)}
            aria-label={t('successivo')}
          >
            <Icon name="chevron_right" style={{ fontSize: '20px' }} />
          </button>
        )}
        
        <div 
          ref={sliderRef}
          className="row flex-nowrap carousel-container"
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            position: 'relative'
          }}
        >
          {nearbyPois.map(renderPoiCard)}
        </div>
      </div>

      <div className="d-flex justify-content-center mt-3" style={{ gap: '8px' }}>
        {indicators}
      </div>
    </section>
  );
};

export default React.memo(PoiSlider);